import { useCallback, useEffect, useMemo, useState } from 'react';

import { useUserData, useUserRole } from '@givebacks/api/core';
import { Cart, storeKeys, useRefundCart, useShowCart } from '@givebacks/api/store';
import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import { centsToDollar } from 'core/Utils';
import {
  Alert,
  ButtonMantine,
  Group,
  InfoListCard,
  Modal,
  ModalProps,
  MultiSelect,
  RadioMantine,
  SimpleGrid,
  Stack,
  Text,
  Title,
  notifications,
} from 'ui';
import { UseFormReturnType, useForm } from 'ui/Form';
import { IconWarning } from 'ui/Icons';

type Props = {
  opened: boolean;
  onClose: () => void;
  cartId: string;
  onSuccess?: (cart?: Cart) => void;
  causeId?: string;
};

type RefundModalContentProps = {
  onClose: () => void;
  cartId: string;
  onSuccess?: (cart?: Cart) => void;
  causeId?: string;
};

type RefundOrderConfirmationProps = {
  onClose: () => void;
  form: UseFormReturnType<RefundFormType>;
  cartId: string;
  isShowConfirmation: boolean;
  setIsShowConfirmation: (value: boolean) => void;
  onSuccess?: (cart?: Cart) => void;
  isSingleItem: boolean;
  causeId?: string;
};

type RefundFormType = {
  refundType: 'full' | 'partial';
  refundItems: string[];
};

const useShouldStopEscape = () => {
  const isRefundProcessing = useIsMutating({ mutationKey: ['carts', 'refund'] });
  return useMemo(() => isRefundProcessing > 0, [isRefundProcessing]);
};

const RefundOrderModalFooter = ({
  onClose,
  form,
  cartId,
  setIsShowConfirmation,
  onSuccess,
  isSingleItem,
  causeId,
  isShowConfirmation,
}: RefundOrderConfirmationProps) => {
  const { mutate: refundCart, isPending: isLoading } = useRefundCart();
  const queryClient = useQueryClient();
  const isRefundProcessing = useShouldStopEscape();

  const onRefund = useCallback(() => {
    refundCart(
      { cartId: cartId, causeId: causeId, cartItemIds: form.values.refundItems },
      {
        onSuccess: (cartData) => {
          notifications.show({ message: 'Refund completed successfully', variant: 'success' });
          void queryClient.invalidateQueries({
            queryKey: storeKeys.id(causeId).carts.id(cartId).all,
          });

          onSuccess?.(cartData?.cart);
          onClose();
        },
        onError() {
          notifications.show({ message: 'Error refunding', variant: 'error' });
        },
      }
    );
  }, [cartId, causeId, form.values.refundItems, onSuccess, onClose, queryClient, refundCart]);

  const handleCancel = useCallback(() => {
    if (isShowConfirmation) {
      if (isSingleItem) {
        onClose();
      } else {
        setIsShowConfirmation(false);
      }
    } else {
      onClose();
    }
  }, [isShowConfirmation, isSingleItem, onClose, setIsShowConfirmation]);

  const handleRefund = useCallback(() => {
    if (isShowConfirmation) {
      onRefund();
    } else {
      setIsShowConfirmation(true);
    }
  }, [isShowConfirmation, onRefund, setIsShowConfirmation]);

  const isActionDisabled = useMemo(() => {
    if (isShowConfirmation) {
      return isLoading;
    }
    return isRefundProcessing;
  }, [isShowConfirmation, isLoading, isRefundProcessing]);

  const isRefundDisabled = useMemo(() => {
    if (!isShowConfirmation) {
      return form.values.refundType === 'partial' && !form.values.refundItems.length;
    }
    return false;
  }, [isShowConfirmation, form.values.refundType, form.values.refundItems]);

  const RefundCTA = useMemo(() => {
    if (isShowConfirmation) {
      return 'Refund Payment';
    }
    return `Refund${form.values.refundType === 'partial' ? ' Item(s)' : ''}`;
  }, [isShowConfirmation, form.values.refundType]);

  return (
    <Group justify="flex-end">
      <ButtonMantine size="md" variant="subtle" onClick={handleCancel} disabled={isActionDisabled}>
        Cancel
      </ButtonMantine>
      <ButtonMantine
        size="md"
        color="cerise"
        onClick={handleRefund}
        loading={isActionDisabled}
        disabled={isRefundDisabled}
      >
        {RefundCTA}
      </ButtonMantine>
    </Group>
  );
};

const RefundOrderModalContent = ({
  onClose,
  cartId,
  onSuccess,
  causeId,
}: RefundModalContentProps) => {
  const { data: cartData } = useShowCart({ cartId: cartId, causeId });
  const { data: userData } = useUserData();

  const [isShowConfirmation, setIsShowConfirmation] = useState(false);

  const { data: roleData } = useUserRole({ causeId });
  const isFaculty = roleData?.role?.name === 'faculty';
  const isAllCreatedByCurrentUser = useMemo(
    () => cartData?.cart_items?.every((item) => item.store_item?.user?.uuid === userData?.uuid),
    [cartData?.cart_items, userData?.uuid]
  );
  const isFullRefundDisabled = useMemo(
    () => isFaculty && !isAllCreatedByCurrentUser,
    [isFaculty, isAllCreatedByCurrentUser]
  );

  const hasProductFundraiser = useMemo(() => {
    return (
      cartData?.cart_items?.some(
        (item) => item.store_item?.type === 'StoreItems::ProductFundraiser'
      ) ?? false
    );
  }, [cartData?.cart_items]);

  const isPartialRefund = useMemo(() => {
    return cartData?.status === 'partially_refunded';
  }, [cartData?.status]);

  const form = useForm<RefundFormType>({
    initialValues: {
      refundType: 'partial',
      refundItems: [],
    },
  });

  const cartItemsRefundTotal = useMemo(() => {
    return cartData?.cart_items
      ?.filter((item) => form.values.refundItems.includes(item.uuid ?? ''))
      .reduce((acc, item) => acc + item.total, 0);
  }, [cartData?.cart_items, form.values.refundItems]);

  const cartRefundTotal = useMemo(() => {
    if (form.values.refundType === 'full') {
      return cartData?.total;
    } else if (form.values.refundType === 'partial') {
      return cartItemsRefundTotal;
    }

    return 0;
  }, [form.values.refundType, cartItemsRefundTotal, cartData?.total]);

  // Filter out ProductFundraiser items since they are non-refundable, and already refunded items
  const selectData = useMemo(() => {
    let reverseItems = cartData?.cart_items?.filter((item) => {
      return item.store_item?.type !== 'StoreItems::ProductFundraiser' && item.reversed_at === null;
    });
    if (isFaculty) {
      // Faculty users can only refund items they created
      reverseItems = reverseItems?.filter((item) => item.store_item?.user?.uuid === userData?.uuid);
    }

    return reverseItems?.map((item) => ({
      value: item.uuid ?? '',
      label: `${item.store_item?.name} - ${centsToDollar(item.total)}`,
    }));
  }, [cartData?.cart_items, isFaculty]);

  useEffect(() => {
    if (cartData?.cart_items?.length === 1) {
      setIsShowConfirmation(true);
    }
  }, [cartData?.cart_items?.length]);

  useEffect(() => {
    form.setValues({
      refundType:
        hasProductFundraiser || isPartialRefund || isFullRefundDisabled ? 'partial' : 'full',
      refundItems: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProductFundraiser, isPartialRefund, isFullRefundDisabled]);

  if (!cartData) return null;

  return (
    <Stack gap="lg">
      {isShowConfirmation && (
        <Stack gap="xs">
          <Title order={4}>Would you like to refund this payment?</Title>
          <Text type="sub" c="gray">
            It may take up to 5-7 days for the refund to appear in their bank account.
          </Text>
        </Stack>
      )}
      <InfoListCard color="blue">
        <InfoListCard.Item label="Refund Amount" detail={centsToDollar(cartRefundTotal)} />
      </InfoListCard>
      {!isShowConfirmation && (
        <>
          {!hasProductFundraiser && !isPartialRefund && (
            <RadioMantine.Group
              name="refundType"
              label="Refund Type"
              size="md"
              {...form.getInputProps('refundType')}
            >
              <SimpleGrid cols={2}>
                <RadioMantine value="full" label="Full refund" disabled={isFullRefundDisabled} />
                <RadioMantine value="partial" label="Partial refund" />
              </SimpleGrid>
            </RadioMantine.Group>
          )}
          <Stack gap="xs">
            <Alert.Transition
              mounted={hasProductFundraiser}
              type="error"
              title="Product fundraiser purchases are non-refundable."
            >
              Continue with partial refund to refund other items.
            </Alert.Transition>
            <Alert.Transition
              mounted={isPartialRefund}
              type="error"
              title="A full refund is not available since some item(s) have already been refunded."
            >
              Select from available line items below for additional refunds.
            </Alert.Transition>
            <Alert.Transition mounted={form.values.refundType === 'partial'} icon={<IconWarning />}>
              Partial refunds do not refund order fees
            </Alert.Transition>
          </Stack>
          {form.values.refundType === 'partial' && (
            <MultiSelect
              label="Line Item(s) to Refund"
              comboboxProps={{ withinPortal: true }}
              placeholder="Select one or more items"
              data={selectData}
              {...form.getInputProps('refundItems')}
            />
          )}
        </>
      )}
      <RefundOrderModalFooter
        onClose={onClose}
        form={form}
        cartId={cartId}
        setIsShowConfirmation={setIsShowConfirmation}
        onSuccess={onSuccess}
        isSingleItem={cartData?.cart_items?.length === 1}
        causeId={causeId}
        isShowConfirmation={isShowConfirmation}
      />
    </Stack>
  );
};

const RefundOrderModal = ({ onClose, cartId, onSuccess, causeId, ...rest }: Props & ModalProps) => {
  const isRefundProcessing = useShouldStopEscape();

  return (
    <Modal
      title="Refund Payment"
      size="lg"
      {...rest}
      onClose={() => {
        if (isRefundProcessing) return;
        onClose?.();
      }}
    >
      <RefundOrderModalContent
        onClose={onClose}
        cartId={cartId}
        onSuccess={onSuccess}
        causeId={causeId}
      />
    </Modal>
  );
};

export { RefundOrderModal };
